import { apiClient, ApiRequestOptions } from "../../../../utils/apiClient";
import { NGROK } from "../../../../APIs";
import { myLocalStorage } from "../../../StorageHelper";

const BASE = NGROK;

function enc(v: string): string {
  return encodeURIComponent(v);
}

function gw<T = any>(opts: ApiRequestOptions): Promise<T> {
  const raw = myLocalStorage.getItem("latestTenant");
  const tenant = typeof raw === "string" ? raw : raw?.tenantName || "";
  return apiClient<T>({
    ...opts,
    headers: {
      ...opts.headers,
      "X-WS-Tenant": tenant,
    },
  });
}

export const GatewayDashboardApi = {
  summary: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/dashboard/summary` }),
  health: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/dashboard/health` }),
  inFlight: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/dashboard/in-flight` }),
};

function mapServerConfig(s: any) {
  return {
    ...s,
    name: s.serverName || s.name,
    transportType: s.type || s.transportType || "STREAMABLE_HTTP",
  };
}

export const GatewayServerConfigApi = {
  list: () =>
    gw<any[]>({ method: "GET", url: `${BASE}/api/admin/mcp-servers` }).then(
      (arr) => (Array.isArray(arr) ? arr.map(mapServerConfig) : [])
    ),
  get: (name: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/mcp-servers/${enc(name)}` }).then(mapServerConfig),
  create: (body: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/mcp-servers`, data: body }),
  update: (name: string, body: any) =>
    gw<any>({ method: "PUT", url: `${BASE}/api/admin/mcp-servers/${enc(name)}`, data: body }),
  remove: (name: string) =>
    gw<any>({ method: "DELETE", url: `${BASE}/api/admin/mcp-servers/${enc(name)}` }),
  connect: (name: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/mcp-servers/${enc(name)}/connect` }),
  disconnect: (name: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/mcp-servers/${enc(name)}/disconnect` }),
  reconnect: (name: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/mcp-servers/${enc(name)}/reconnect` }),
};

export const GatewayAuthConfigApi = {
  get: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/auth-config` }),
  create: (body: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/auth-config`, data: body }),
  update: (body: any) =>
    gw<any>({ method: "PUT", url: `${BASE}/api/admin/auth-config`, data: body }),
  remove: () =>
    gw<any>({ method: "DELETE", url: `${BASE}/api/admin/auth-config` }),
  validate: (body: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/auth-config/validate`, data: body }),
  discover: (body: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/auth-config/discover`, data: body }),
  refreshJwks: () =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/auth-config/refresh-jwks` }),
};

export const GatewayServersApi = {
  list: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/mcp/servers` }),
  tools: (name: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/mcp/servers/${enc(name)}/tools` }),
  resources: (name: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/mcp/servers/${enc(name)}/resources` }),
  prompts: (name: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/mcp/servers/${enc(name)}/prompts` }),
  callTool: (name: string, tool: string, input: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/mcp/servers/${enc(name)}/tools/${enc(tool)}`, data: input }),
};

function buildQS(params?: Record<string, any>): string {
  const qs = new URLSearchParams();
  if (!params) return "";
  Object.entries(params).forEach(([k, v]) => {
    if (v === null || v === undefined || v === "") return;
    if (Array.isArray(v)) {
      v.forEach((item) => {
        if (item !== null && item !== undefined && item !== "") qs.append(k, String(item));
      });
    } else {
      qs.append(k, String(v));
    }
  });
  return qs.toString();
}

export const GatewayAuditApi = {
  logs: (params?: Record<string, any>) => {
    const qs = buildQS(params);
    return gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/logs?${qs}` });
  },
  logById: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/logs/${id}` }),
  correlation: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/logs/correlation/${enc(id)}` }),
  stats: (since?: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/stats${since ? "?since=" + enc(since) : ""}` }),
  timeline: (hours: number = 24) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/stats/timeline?hours=${hours}` }),
  filters: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/filters` }),
  sessionEvents: (sessionId: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/audit/logs?sessionId=${enc(sessionId)}&size=100` }),
};

export const GatewayAgentsApi = {
  list: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/agents` }),
  get: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/agents/${id}` }),
  sessions: (id: string, params?: Record<string, any>) => {
    const qs = buildQS(params);
    return gw<any>({ method: "GET", url: `${BASE}/api/admin/agents/${id}/sessions${qs ? `?${qs}` : ""}` });
  },
  sessionFilters: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/agents/${id}/sessions/filters` }),
  summary: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/agents/summary` }),
  approve: (id: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/agents/${id}/approve` }),
  block: (id: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/agents/${id}/block` }),
  analytics: (id: string, hours: number = 24) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/agents/${id}/analytics?hours=${hours}` }),
  liveSessions: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/agents/sessions/live` }),
  sessionTimeline: (sid: string, page: number = 0, size: number = 50) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/agents/sessions/${sid}/timeline?page=${page}&size=${size}` }),
};

export const GatewayPoliciesApi = {
  list: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/policies` }),
  get: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/policies/${id}` }),
  create: (body: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/policies`, data: body }),
  update: (id: string, body: any) =>
    gw<any>({ method: "PUT", url: `${BASE}/api/admin/policies/${id}`, data: body }),
  remove: (id: string) =>
    gw<any>({ method: "DELETE", url: `${BASE}/api/admin/policies/${id}` }),
  toggle: (id: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/policies/${id}/toggle` }),
  reload: () =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/policies/reload` }),
  stats: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/policies/stats` }),
  validate: (text: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/policies/validate`, data: { policyText: text } }),
  chat: (data: string | any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/policies/chat`, data: typeof data === "string" ? { prompt: data } : data }),
  chatSave: (data: string | any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/policies/chat/save`, data: typeof data === "string" ? { prompt: data } : data }),
};

export const GatewayProfilesApi = {
  list: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/capability-profiles` }),
  templates: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/capability-profiles/templates` }),
  get: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/capability-profiles/${id}` }),
  create: (body: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/capability-profiles`, data: body }),
  update: (id: string, body: any) =>
    gw<any>({ method: "PUT", url: `${BASE}/api/admin/capability-profiles/${id}`, data: body }),
  remove: (id: string) =>
    gw<any>({ method: "DELETE", url: `${BASE}/api/admin/capability-profiles/${id}` }),
  assign: (profileId: string, agentId: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/capability-profiles/${profileId}/assign/${agentId}` }),
  unassign: (profileId: string, agentId: string) =>
    gw<any>({ method: "DELETE", url: `${BASE}/api/admin/capability-profiles/${profileId}/assign/${agentId}` }),
  preview: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/capability-profiles/${id}/preview` }),
  agentAccess: (agentId: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/capability-profiles/agent-access/${agentId}` }),
  availableServers: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/capability-profiles/available-servers` }),
  chat: (data: string | any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/capability-profiles/chat`, data: typeof data === "string" ? { prompt: data } : data }),
  chatStatus: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/capability-profiles/chat/status` }),
};

export const GatewayCustomAttributesApi = {
  list: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/custom-attributes` }),
  get: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/custom-attributes/${id}` }),
  create: (body: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/custom-attributes`, data: body }),
  update: (id: string, body: any) =>
    gw<any>({ method: "PUT", url: `${BASE}/api/admin/custom-attributes/${id}`, data: body }),
  remove: (id: string) =>
    gw<any>({ method: "DELETE", url: `${BASE}/api/admin/custom-attributes/${id}` }),
  toggle: (id: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/custom-attributes/${id}/toggle` }),
  stats: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/custom-attributes/stats` }),
  sources: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/custom-attributes/sources` }),
};

export const GatewayHumanUsersApi = {
  list: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users` }),
  get: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/${id}` }),
  sessions: (id: string, params?: Record<string, any>) => {
    const qs = buildQS(params);
    return gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/${id}/sessions${qs ? `?${qs}` : ""}` });
  },
  sessionFilters: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/${id}/sessions/filters` }),
  agents: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/${id}/agents` }),
  summary: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/summary` }),
  approve: (id: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/human-users/${id}/approve` }),
  block: (id: string, body?: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/human-users/${id}/block`, data: body }),
  unblock: (id: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/human-users/${id}/unblock` }),
  search: (q: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/search?q=${enc(q)}` }),
  lineage: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/${id}/lineage` }),
  activeNow: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/active-now` }),
  riskAssessment: (id: string, hours: number = 24) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/${id}/risk-assessment?hours=${hours}` }),
  analytics: (id: string, hours: number = 24) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/human-users/${id}/analytics?hours=${hours}` }),
};

export const GatewayNhisApi = {
  list: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis` }),
  get: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis/${id}` }),
  sessions: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis/${id}/sessions` }),
  agents: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis/${id}/agents` }),
  summary: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis/summary` }),
  approve: (id: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/nhis/${id}/approve` }),
  block: (id: string, body?: any) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/nhis/${id}/block`, data: body }),
  unblock: (id: string) =>
    gw<any>({ method: "POST", url: `${BASE}/api/admin/nhis/${id}/unblock` }),
  search: (q: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis/search?q=${enc(q)}` }),
  lineage: (id: string) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis/${id}/lineage` }),
  activeNow: () =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis/active-now` }),
  riskAssessment: (id: string, hours: number = 24) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis/${id}/risk-assessment?hours=${hours}` }),
  analytics: (id: string, hours: number = 24) =>
    gw<any>({ method: "GET", url: `${BASE}/api/admin/nhis/${id}/analytics?hours=${hours}` }),
};
